/* eslint-disable react-hooks/exhaustive-deps */
import { Button, Card, CardContent, FormControlLabel, Switch, Tooltip, withStyles, withTheme } from '@material-ui/core';
import { Box, Typography } from '@mui/material';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { DELETE_CLIENT_DIVISION_FAILURE, GET_CLIENT_DETAILS_FAILURE, GET_CLIENT_DIVISIONS_FAILURE, POST_CLIENT_DIVISION_FAILURE, UPDATE_CLIENT_DIVISION_FAILURE, UPDATE_CLIENT_FAILURE } from '../../types';
import { deleteClientDivision, postClientDivision, updateClientDivision, getAllClientDivisions, getClientDetails, updateClient } from '../../clients.actions';
import CrudTable from '../crudTable.component';
import { isIdEqual } from 'modules/enumerations/enumerationHelpers';
import { showToast } from 'modules/layout/layout.actions';
import { TOAST_MESSAGE_SEVERITY_ERROR, TOAST_MESSAGE_SEVERITY_SUCCESS } from 'modules/layout/types';

const styles = (theme) => {
  return {
    row: {
      backgroundColor: theme.palette.common.white,
    },
    accordionStyleContacts: {
      maxWidth: 1050,
    },
    inputGroup: {
      display: 'inline-flex',
      justifyContent: 'space-between',
    },
    root: {
      maxWidth: 170,
      marginRight: 10,
    },
    extRoot: {
      width: 115,
      marginRight: 10,
    },
    primaryText: {
      paddingTop: 2,
      paddingRight: 5,
      marginRight: 16,
    },
    newRow: {
      display: 'flex',
      flexDirection: 'column',
      maxWidth: 1050,
    },
  };
};

const ClientDivisionsTable = props => {

  const { clientDetails, clientDivisions, clientRecordId, clientAddresses, provinceEnums, hideEdit = false } = props;
  const [hasMultipleDivisions, setHasMultipleDivisions] = useState(Boolean(clientDetails?.hasMultipleDivisions));
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setHasMultipleDivisions(Boolean(clientDetails?.hasMultipleDivisions));
  }, [clientDetails?.hasMultipleDivisions, clientRecordId]);

  const saveMultipleDivisions = async () => {
    if (hideEdit) return;
    setIsSaving(true);
    const resp = await props.updateClient({ ...clientDetails, hasMultipleDivisions });
    if (resp.type === UPDATE_CLIENT_FAILURE) {
      props.showToast('Failed to update client divisions setting, please try again.', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      setIsSaving(false);
      return;
    }

    const getResp = await props.getClientDetails(clientRecordId);
    if (getResp.type === GET_CLIENT_DETAILS_FAILURE) {
      props.showToast('Failed to refresh client details, please try again.', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
    } else {
      props.showToast('Client divisions setting updated successfully.', { severity: TOAST_MESSAGE_SEVERITY_SUCCESS });
    }
    setIsSaving(false);
  };

  const provinceOptions = provinceEnums && Array.isArray(provinceEnums) ? provinceEnums.filter(s => s.isActive) : [];

  const formatAddressLabel = (address) => {
    if (!address) return '';
    const { addressLine1, city, provinceId, zipcode } = address;
    const state = provinceOptions ? provinceOptions.find(s => isIdEqual(s.id, provinceId))?.title || '' : '';
    return `${addressLine1}, ${city}, ${state} ${zipcode}`;
  }

  const mappedClientAddresses = clientAddresses && Array.isArray(clientAddresses) ? clientAddresses.map(address => ({
    id: address.clientAddressId,
    title: formatAddressLabel(address),
  })) : [];

  const adjustedColumns = [
    { accessorKey: 'divisionName', header: 'Div. Name', type: 'text', required: true, size: 60, enableColumnDragging: false, enableColumnActions: false, enableResizing: false },
    { accessorKey: 'divisionCode', header: 'Div. Code', type: 'text', required: true, size: 55, enableColumnDragging: false, enableColumnActions: false, enableResizing: false },
    { accessorKey: 'existInAcumatica', header: 'In Acu.', type: 'text', required: false, size: 45, hideInModel: true, enableColumnDragging: false, enableColumnActions: false, enableResizing: false },
    { accessorKey: 'primaryAddressId', header: 'Division Address', type: 'dropdown-v2', options: mappedClientAddresses, required: false, hideInTable: true },
    { accessorKey: 'acumaticaProjectId', header: 'Acumatica Project ID', type: 'text', required: false, disabled: true, hideInTable: true, size: 70, enableColumnDragging: false, enableColumnActions: false, enableResizing: false },
  ];

  const divisionsRows = clientDivisions && Array.isArray(clientDivisions) ? clientDivisions.map(division => ({
    ...division,
    existInAcumatica: division.acumaticaProjectId && division.acumaticaProjectId !== '' ? 'Yes' : 'No'
  })) : [];

  const updateRow = async (division, values) => {
    const id = division.original.clientDivisionId;

    let newValues = {
      clientDivisionId: id,
      clientRecordId: clientRecordId,
      divisionName: values.divisionName,
      divisionCode: values.divisionCode,
      divisionKey: values.divisionKey,
      addedOn: division.original.addedOn,
      addedBy: division.original.addedBy,
      modifiedOn: new Date(),
      modifiedBy: 1,
      primaryAddressId: values.primaryAddressId === '' ? null : values.primaryAddressId
    }

    const resp = await props.updateClientDivision(newValues);
    if (resp.type === UPDATE_CLIENT_DIVISION_FAILURE) {
      return false;
    }
    else {
      const getResp = await props.getAllClientDivisions(clientRecordId);
      if (getResp.type === GET_CLIENT_DIVISIONS_FAILURE) {
        return false;
      } else {
        return true;
      }
    }
  };

  const createRow = async (values) => {
    let newValues = {
      clientRecordId: clientRecordId,
      divisionName: values.divisionName,
      divisionCode: values.divisionCode,
      divisionKey: values.divisionKey,
      addedOn: new Date(),
      addedBy: 1,
      modifiedOn: new Date(),
      modifiedBy: 1,
      primaryAddressId: values.primaryAddressId === '' ? null : values.primaryAddressId
    }
    const resp = await props.postClientDivision(newValues);
    if (resp.type === POST_CLIENT_DIVISION_FAILURE) {
      return false;
    } else {
      const getResp = await props.getAllClientDivisions(clientRecordId);
      if (getResp.type === GET_CLIENT_DIVISIONS_FAILURE) {
        return false;
      } else {
        return true;
      }
    }
  };

  const deleteRow = async (division) => {
    const id = division.original.clientDivisionId;
    const resp = await props.deleteClientDivision(id);
    if (resp.type === DELETE_CLIENT_DIVISION_FAILURE) {
      return false;
    } else {
      const getResp = await props.getAllClientDivisions(clientRecordId);
      if (getResp.type === GET_CLIENT_DIVISIONS_FAILURE) {
        return false;
      } else {
        return true;
      }
    }
  };

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <Tooltip title={'File Contract'} key={'clientDivisions'} placement="right">
            <FontAwesomeIcon icon={['fas', 'location-dot']} size="2x" />
          </Tooltip>
          <Typography variant="h5" sx={{ paddingLeft: '20px', fontSize: '1.3125rem', fontWeight: 'bold' }}>
            Divisions
          </Typography>
          <FormControlLabel
            control={<Switch checked={hasMultipleDivisions} onChange={event => setHasMultipleDivisions(event.target.checked)} color="primary" disabled={hideEdit || isSaving} />}
            label="Has Multiple Divisions"
            style={{ marginLeft: 'auto' }}
          />
          {hasMultipleDivisions !== Boolean(clientDetails?.hasMultipleDivisions) && (
            <Button onClick={saveMultipleDivisions} disabled={hideEdit || isSaving} color="primary" variant="contained">Save</Button>
          )}
        </Box>
        <CrudTable
          editingMode={'modal'} // Can be changed to 'row' to edit inline 
          columns={adjustedColumns}
          rows={divisionsRows ? divisionsRows : []}
          updateRow={updateRow}
          createRow={createRow}
          deleteRow={deleteRow}
          hideAdd={hideEdit || !clientDetails?.hasMultipleDivisions || isSaving}
          hideEdit={hideEdit}
          hideDelete={hideEdit}
        />
      </CardContent>
    </Card>

  );
};

ClientDivisionsTable.propTypes = {
  hideEdit: PropTypes.bool,
  classes: PropTypes.object.isRequired,
  clientRecordId: PropTypes.number,
  clientDetails: PropTypes.object.isRequired,
  formikProps: PropTypes.object.isRequired,
  internalUsers: PropTypes.array,
  clientDivisions: PropTypes.array,
};

const mapStateToProps = (state) => {
  const { clientContactsLoading } = state.clients;
  return {
    clientContactsLoading,
  };
};

export default compose(
  withStyles(styles),
  withTheme,
  connect(mapStateToProps, {
    getAllClientDivisions,
    deleteClientDivision,
    postClientDivision,
    updateClientDivision,
    getClientDetails,
    updateClient,
    showToast,
  }),
)(ClientDivisionsTable);